// @ts-check
import React, { useCallback, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import ListItem from '@material-ui/core/ListItem';
import Divider from '@material-ui/core/Divider';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import Avatar from '@material-ui/core/Avatar';
import { useHistory } from 'react-router-dom';
import { ListItemSecondaryAction } from '@material-ui/core';
import { FixedSizeList } from 'react-window';

import { EmptyComponent } from '../../../../core/utils/components/empty-component';
import Chip from '@material-ui/core/Chip';

// Keep ROW_HEIGHT in sync with the rendered ListItem; MUI two-line ListItem
// with avatar + secondary action sits at ~72px including divider.
const ROW_HEIGHT = 72;
// Cap viewport so tiny fleets don't get a giant empty list and huge fleets
// stay scrollable inside the page chrome.
const MAX_LIST_HEIGHT = 640;
const MIN_LIST_HEIGHT = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    backgroundColor: theme.palette.background.paper
  },
  inline: {
    display: 'inline'
  },
  granted: {
    color: '#4caf50'
  }
}));

/**
 * Deterministic initials avatar — avoids one network request per row, which
 * was a primary cause of the tab crash on large fleets. Real avatars are
 * still shown on the driver detail page.
 */
function initialFor(name) {
  if (!name) return '?';
  const trimmed = name.trim();
  return trimmed.length ? trimmed[0].toUpperCase() : '?';
}

const DriverRow = React.memo(function DriverRow({ index, style, data }) {
  const { drivers, onSelect, lastIndex } = data;
  const driver = drivers[index];

  return (
    <div style={style} key={driver.id}>
      <ListItem alignItems="flex-start" button onClick={() => onSelect(driver.id)}>
        <ListItemAvatar>
          <Avatar alt={driver.name}>{initialFor(driver.name)}</Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={`${driver.name}`}
          secondary={<React.Fragment>{driver.email}</React.Fragment>}
        />
        <ListItemSecondaryAction>
          {!driver.blocked && <Chip color="primary" size="small" label={driver.status} />}
          {driver.blocked && <Chip color="secondary" size="small" label={'Blocked'} />}
        </ListItemSecondaryAction>
      </ListItem>
      {index !== lastIndex && <Divider variant="inset" component="li" />}
    </div>
  );
});

export function AlignItemsList({ drivers }) {
  const classes = useStyles();
  const history = useHistory();

  const onSelect = useCallback((id) => history.push(`/drivers/${id}/view`), [history]);

  const itemData = useMemo(
    () => ({ drivers, onSelect, lastIndex: drivers.length - 1 }),
    [drivers, onSelect]
  );

  if (drivers.length === 0) {
    return (
      <div className={classes.root}>
        <EmptyComponent message={'No drivers'} />
      </div>
    );
  }

  // Size the window to the data when small, cap it for large fleets so the
  // surrounding page chrome (search, export, FAB) stays visible.
  const listHeight = Math.min(
    MAX_LIST_HEIGHT,
    Math.max(MIN_LIST_HEIGHT, drivers.length * ROW_HEIGHT)
  );

  return (
    <div className={classes.root}>
      <FixedSizeList
        height={listHeight}
        itemCount={drivers.length}
        itemSize={ROW_HEIGHT}
        itemData={itemData}
        itemKey={(index, data) => data.drivers[index].id}
        width="100%"
        overscanCount={4}
      >
        {DriverRow}
      </FixedSizeList>
    </div>
  );
}
